import { KEYS_TO_REMOVE_FOR_OVERALL_ANALYSIS_TABLE } from "../../constants/constants";
import { capitalizeFirstLetterAndRemainingSmallForEachWord } from "../commonUtils";
import { formatTrendHeaderChips } from "../designerPage-utils";
import { formatCompactNumber } from "../formatCompactNumber";

/** Fixed columns: label in UI must match table headers / PDF exactly. */
export const OVERALL_ANALYSIS_FIXED_TABLE_COLUMNS: ReadonlyArray<{
  headerLabel: string;
  formatKey: string;
}> = [
  { headerLabel: "Trend No.", formatKey: "tNo" },
  { headerLabel: "Contribution", formatKey: "contribution" },
  { headerLabel: "Response", formatKey: "impact" },
  { headerLabel: "Impact Score", formatKey: "Impact Score" },
  { headerLabel: "Impact Value", formatKey: "Impact Value" },
];

const IMPACT_VALUE_FORMAT_KEY = "Impact Value";

/** Fixed columns before dynamic attribute columns (Impact Value rendered last). */
export const OVERALL_ANALYSIS_LEADING_FIXED_TABLE_COLUMNS =
  OVERALL_ANALYSIS_FIXED_TABLE_COLUMNS.filter(
    ({ formatKey }) => formatKey !== IMPACT_VALUE_FORMAT_KEY
  );

/** Impact Value — last table column, after dynamic attributes. */
export const OVERALL_ANALYSIS_TRAILING_FIXED_TABLE_COLUMNS =
  OVERALL_ANALYSIS_FIXED_TABLE_COLUMNS.filter(
    ({ formatKey }) => formatKey === IMPACT_VALUE_FORMAT_KEY
  );

/** Graph hover: Contribution + Response only (Trend No. on axis). */
export const OVERALL_ANALYSIS_FIXED_TOOLTIP_COLUMNS =
  OVERALL_ANALYSIS_FIXED_TABLE_COLUMNS.filter(
    ({ formatKey }) => formatKey === "contribution" || formatKey === "impact"
  );

export function getOverallAnalysisDynamicColumnKeys(
  row: Record<string, unknown>
): string[] {
  return Object.keys(row).filter(
    key => !KEYS_TO_REMOVE_FOR_OVERALL_ANALYSIS_TABLE.includes(key)
  );
}

export function getOverallAnalysisDisplayColumns(
  dynamicColumns: string[],
  selectedMaterials?: string[]
): string[] {
  const checkMaterialContent = selectedMaterials?.some(
    material => material === "Color"
  );
  const visibleColumns = dynamicColumns.filter(
    column => column !== "Color" || !!checkMaterialContent
  );
  return [
    ...visibleColumns.filter(c => c !== "Color"),
    ...(visibleColumns.includes("Color") ? ["Color"] : []),
  ];
}

export const OVERALL_ANALYSIS_EXCLUDED_DYNAMIC_COLUMNS = new Set([
  "Trend Score",
  "Impact Score",
  "Impact Value",
]);

const OVERALL_ANALYSIS_FABRIC_ATTRIBUTE_COLUMNS = new Set([
  "Wash",
  "Woven Fabric",
  "Knitted Fabric",
]);

const WOVEN_FABRIC_COLUMN = "Woven Fabric";

const OVERALL_ANALYSIS_FABRIC_TABLE_COLUMN_ORDER = [
  WOVEN_FABRIC_COLUMN,
  "Knitted Fabric",
  "Wash",
] as const;

function isFabricMaterialAnalysis(selectedMaterials?: string[]): boolean {
  return !!selectedMaterials?.includes("Fabric");
}

export function getOverallAnalysisTooltipDynamicColumns(
  dynamicColumns: string[],
  selectedMaterials?: string[]
): string[] {
  return getOverallAnalysisDisplayColumns(
    dynamicColumns,
    selectedMaterials
  ).filter(col => !OVERALL_ANALYSIS_EXCLUDED_DYNAMIC_COLUMNS.has(col));
}

/** Dynamic attribute columns for the table (one column each). */
export function getOverallAnalysisTableAttributeColumns(
  dynamicColumns: string[],
  selectedMaterials?: string[]
): string[] {
  const columns = getOverallAnalysisDisplayColumns(
    dynamicColumns,
    selectedMaterials
  ).filter(col => !OVERALL_ANALYSIS_EXCLUDED_DYNAMIC_COLUMNS.has(col));

  if (!isFabricMaterialAnalysis(selectedMaterials)) {
    return columns;
  }

  const fabricColumns = OVERALL_ANALYSIS_FABRIC_TABLE_COLUMN_ORDER.filter(col =>
    columns.includes(col)
  );
  const otherColumns = columns.filter(
    col => !OVERALL_ANALYSIS_FABRIC_ATTRIBUTE_COLUMNS.has(col)
  );
  return [...fabricColumns, ...otherColumns];
}

export function getOverallAnalysisFixedColumnValue(
  row: Record<string, unknown>,
  formatKey: string
): unknown {
  const value = row[formatKey];
  if (value != null && value !== "") return value;
  if (formatKey === "Impact Value") return row.impact_value;
  if (formatKey === "Impact Score") return row.impact_score;
  return value;
}

function isEmptyOrNaDisplay(formatted: string, raw: unknown): boolean {
  if (!formatted || formatted === "N/A") return true;
  if (raw == null) return true;
  if (typeof raw === "string" && raw.trim().toLowerCase() === "n/a") {
    return true;
  }
  return false;
}

function getFirstNonNaDynamicBarLabel(
  row: Record<string, unknown>,
  dynamicColumns: string[]
): string {
  for (const column of dynamicColumns) {
    const raw = row[column];
    const formatted = formatOverallAnalysisCellDisplay(column, raw);
    if (!isEmptyOrNaDisplay(formatted, raw)) {
      return formatted;
    }
  }
  return "";
}

/** Graph bar label: Fabric → Woven Fabric only (including N/A). */
export function getOverallAnalysisGraphBarLabel(
  row: Record<string, unknown>,
  dynamicColumns: string[],
  selectedMaterials?: string[]
): string {
  if (isFabricMaterialAnalysis(selectedMaterials)) {
    return formatOverallAnalysisCellDisplay(
      WOVEN_FABRIC_COLUMN,
      row[WOVEN_FABRIC_COLUMN]
    );
  }
  return getFirstNonNaDynamicBarLabel(row, dynamicColumns);
}

export function formatOverallAnalysisCellDisplay(
  column: string,
  raw: unknown
): string {
  if (raw == null) return "N/A";
  if (column === "Impact Value") {
    const n = Number(raw);
    return Number.isNaN(n) ? "N/A" : formatCompactNumber(n);
  }
  if (column === "Impact Score") {
    const n = Number(raw);
    return Number.isNaN(n) ? "N/A" : n.toFixed(0);
  }
  if (column === "impact") {
    return capitalizeFirstLetterAndRemainingSmallForEachWord(String(raw));
  }
  if (column === "contribution") {
    const n = Number(raw);
    return Number.isNaN(n) ? "N/A" : n.toFixed(2);
  }
  if (column === "tNo") {
    return String(raw);
  }
  if (typeof raw === "string") {
    return formatTrendHeaderChips(raw);
  }
  return Number(raw).toFixed(2);
}
